import { useRecoilState, useRecoilValue } from 'recoil';
import {
  aiSchedulerChatMessagesAtom,
  aiSchedulerChatSessionIdAtom,
  aiSchedulerStatusTextAtom,
  AssistantChatMessage,
  ChatSuggestedEvent,
  CTAOption,
  SessionResponse,
} from '../data-access';
import { useCallback, useMemo, useRef, useState } from 'react';
import { trackEventMixpanel } from '../utils';

interface UseAISchedulerChatProps {
  onReschedule: (
    text: string,
    chatSessionId?: string,
    onMessage?: (delta: string) => void,
    onQuestion?: (question: unknown) => void,
    hiddenContext?: Record<string, unknown>,
    onWholeMessage?: (message: string, textId: number) => void,
    onSuggestedBlocks?: (blocks: ChatSuggestedEvent[]) => void
  ) => Promise<SessionResponse | undefined>;
  enabled?: boolean;
}

const START_MESSAGE_PROMPT =
  'How would you like me to adjust your schedule? You can ask me to move events, add breaks, or make other changes.';
const createStartMessage = (content: string): AssistantChatMessage => ({
  messageId: '1',
  content,
  role: 'assistant',
  sentAt: new Date().toISOString(),
});

export const useAISchedulerChat = ({ onReschedule, enabled = true }: UseAISchedulerChatProps) => {
  const [messages, setMessages] = useRecoilState(aiSchedulerChatMessagesAtom);
  const [chatSessionId, setChatSessionId] = useRecoilState(aiSchedulerChatSessionIdAtom);
  const loadingStatusText = useRecoilValue(aiSchedulerStatusTextAtom);
  const [isReplying, setIsReplying] = useState(false);
  const [isQuestionPending, setIsQuestionPending] = useState(false);
  const streamingMessageIdRef = useRef<string | null>(null);
  const streamingContentRef = useRef<string>('');
  const sequenceMessageIdsRef = useRef<Map<number, string>>(new Map());
  // Bumped on closeChat so in-flight stream callbacks cannot repopulate a
  // cleared thread after New Chat (abort races with late setMessages).
  const chatGenerationRef = useRef(0);

  const resetStreamingRefs = useCallback(() => {
    streamingMessageIdRef.current = null;
    streamingContentRef.current = '';
    sequenceMessageIdsRef.current.clear();
  }, []);

  const isCurrentGeneration = useCallback((generation: number) => {
    return chatGenerationRef.current === generation;
  }, []);

  const removeActiveStreamingMessages = useCallback(() => {
    const messageIdsToRemove = [
      streamingMessageIdRef.current,
      ...Array.from(sequenceMessageIdsRef.current.values()),
    ].filter((messageId): messageId is string => !!messageId);

    if (messageIdsToRemove.length > 0) {
      setMessages((prev) => prev.filter((msg) => !messageIdsToRemove.includes(msg.messageId)));
    }
    resetStreamingRefs();
  }, [resetStreamingRefs, setMessages]);

  const handleQuestionEvent = useCallback(
    (questionData: unknown, generation: number) => {
      if (!isCurrentGeneration(generation)) {
        return;
      }
      const questionSessionId =
        questionData &&
        typeof questionData === 'object' &&
        ('session_id' in questionData || 'sessionId' in questionData)
          ? ((questionData as { session_id?: string; sessionId?: string }).session_id ??
            (questionData as { sessionId?: string }).sessionId)
          : undefined;
      if (questionSessionId) {
        setChatSessionId(questionSessionId);
      }

      // If text was streaming, discard the partial bubble when a question interrupts.
      removeActiveStreamingMessages();

      const questionMessage: AssistantChatMessage = {
        messageId: `question_${Date.now()}`,
        content: '',
        role: 'assistant',
        sentAt: new Date().toISOString(),
        question:
          questionData && typeof questionData === 'object'
            ? (questionData as AssistantChatMessage['question'])
            : undefined,
        isAnswered: false,
      };

      setMessages((prev) => {
        if (!isCurrentGeneration(generation)) {
          return prev;
        }
        return [...prev, questionMessage];
      });
      if (isCurrentGeneration(generation)) {
        setIsQuestionPending(true);
      }
    },
    [isCurrentGeneration, removeActiveStreamingMessages, setChatSessionId, setMessages]
  );

  const recentUserMessage = useMemo(() => {
    return messages.filter((msg) => msg.role === 'user').at(-1);
  }, [messages]);

  const startChat = (summary?: string, runDisplayMessage?: string) => {
    if (!enabled) {
      return;
    }

    chatGenerationRef.current += 1;
    resetStreamingRefs();
    setIsReplying(false);
    setIsQuestionPending(false);

    const formattedSummary = summary?.trim();
    const content = formattedSummary
      ? `${formattedSummary}\n\n${START_MESSAGE_PROMPT}`
      : START_MESSAGE_PROMPT;

    const startMessage = createStartMessage(content);
    if (runDisplayMessage?.trim()) {
      setMessages([
        {
          messageId: `run_${Date.now()}`,
          content: runDisplayMessage.trim(),
          role: 'user',
          sentAt: new Date().toISOString(),
        },
        startMessage,
      ]);
      return;
    }

    setMessages([startMessage]);
  };

  const sendMessage = async (
    text: string,
    hiddenContext?: Record<string, unknown>,
    displayText = text
  ) => {
    if (!enabled) {
      return false;
    }

    const generation = chatGenerationRef.current;
    setIsReplying(true);
    resetStreamingRefs();

    // Add user message with a unique temporary messageId
    const tempMessageId = `temp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        messageId: tempMessageId,
        content: displayText,
        role: 'user' as const,
        sentAt: new Date().toISOString(),
      },
    ]);

    // Handle streaming message deltas
    const handleStreamingDelta = (delta: string) => {
      if (!isCurrentGeneration(generation)) {
        return;
      }
      streamingContentRef.current += delta;

      // Check if we already have a streaming message ID
      if (streamingMessageIdRef.current) {
        // Update existing streaming message
        setMessages((prev) =>
          prev.map((msg) =>
            msg.messageId === streamingMessageIdRef.current
              ? { ...msg, content: streamingContentRef.current }
              : msg
          )
        );
        return;
      }

      // First delta - create new streaming message
      const streamingMessageId = `streaming_${Date.now()}`;
      streamingMessageIdRef.current = streamingMessageId;

      const streamingMessage: AssistantChatMessage = {
        messageId: streamingMessageId,
        content: streamingContentRef.current,
        role: 'assistant',
        sentAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, streamingMessage]);
    };

    const handleWholeMessage = (message: string, textId: number) => {
      if (!isCurrentGeneration(generation)) {
        return;
      }
      const existingMessageId = sequenceMessageIdsRef.current.get(textId);
      if (existingMessageId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.messageId === existingMessageId ? { ...msg, content: message } : msg
          )
        );
        return;
      }

      const sequenceMessageId = `sequence_${textId}_${Date.now()}`;
      sequenceMessageIdsRef.current.set(textId, sequenceMessageId);
      setMessages((prev) => [
        ...prev,
        {
          messageId: sequenceMessageId,
          content: message,
          role: 'assistant',
          sentAt: new Date().toISOString(),
        },
      ]);
    };

    const handleSuggestedBlocks = (blocks: ChatSuggestedEvent[]) => {
      if (!isCurrentGeneration(generation) || blocks.length === 0) {
        return;
      }

      setMessages((prev) => [
        ...prev.filter((msg) => !msg.suggestedBlocks?.length),
        {
          messageId: `suggested_blocks_${Date.now()}`,
          content: '',
          role: 'assistant',
          sentAt: new Date().toISOString(),
          suggestedBlocks: blocks,
        },
      ]);
    };

    try {
      // Call the AI scheduler adjustment with streaming callback
      const newSessions = await onReschedule(
        text,
        chatSessionId ?? undefined,
        handleStreamingDelta,
        (question) => {
          handleQuestionEvent(question, generation);
        },
        hiddenContext,
        handleWholeMessage,
        handleSuggestedBlocks
      );

      if (!isCurrentGeneration(generation)) {
        return false;
      }

      // Update chat session ID if we received a new one
      if (newSessions?.currentSessionId) {
        setChatSessionId(newSessions.currentSessionId);
      }

      // If we have a streaming message, replace it with final message if available
      if (streamingMessageIdRef.current) {
        if (!!newSessions?.messages && newSessions.messages.length > 0) {
          // Replace streaming message with final message
          const finalAssistantMessage = newSessions.messages.find(
            (msg) => msg.role === 'assistant'
          );
          if (finalAssistantMessage) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.messageId === streamingMessageIdRef.current
                  ? { ...msg, ...finalAssistantMessage }
                  : msg
              )
            );
          } else {
            // No final message, keep streaming content
            setMessages((prev) =>
              prev.map((msg) =>
                msg.messageId === streamingMessageIdRef.current
                  ? { ...msg, content: streamingContentRef.current }
                  : msg
              )
            );
          }
        } else {
          // No final messages, keep streaming content
          setMessages((prev) =>
            prev.map((msg) =>
              msg.messageId === streamingMessageIdRef.current
                ? { ...msg, content: streamingContentRef.current }
                : msg
            )
          );
        }
        streamingMessageIdRef.current = null;
      } else if (
        sequenceMessageIdsRef.current.size === 0 &&
        !!newSessions?.messages &&
        newSessions.messages.length > 0
      ) {
        // No streaming message, add server messages normally
        setMessages((prev) => {
          let updatedMessages = [...prev];

          newSessions.messages?.forEach((serverMsg) => {
            if (
              serverMsg.role === 'user' &&
              serverMsg.content === text &&
              updatedMessages.some((msg) => msg.messageId === tempMessageId && msg.role === 'user')
            ) {
              // Replace temp user message with server message
              updatedMessages = updatedMessages.map((msg) =>
                msg.messageId === tempMessageId && msg.role === 'user'
                  ? { ...msg, ...serverMsg }
                  : msg
              );
            } else if (!updatedMessages.some((msg) => msg.messageId === serverMsg.messageId)) {
              // Add new server message if not already present
              updatedMessages.push(serverMsg);
            }
          });

          return updatedMessages;
        });
      }

      trackEventMixpanel('send_ai_scheduler_chat', {
        message: text,
        sessionId: newSessions?.currentSessionId,
      });

      return true;
    } catch (error) {
      if (!isCurrentGeneration(generation)) {
        return false;
      }
      // Remove streaming messages if an error occurred.
      removeActiveStreamingMessages();
      setMessages((prev) => [
        ...prev,
        {
          messageId: `error-${Date.now()}`,
          content: "Sorry, I couldn't process your request. Please try again.",
          role: 'assistant',
          sentAt: new Date().toISOString(),
        },
      ]);

      return false;
    } finally {
      if (isCurrentGeneration(generation)) {
        setIsReplying(false);
        resetStreamingRefs();
      }
    }
  };

  const sendQuestionAnswer = useCallback(
    async (questionMessageId: string, option: CTAOption) => {
      // Optimistically disable the buttons while the answer is in flight.
      setMessages((prev) =>
        prev.map((msg) =>
          msg.messageId === questionMessageId ? { ...msg, isAnswered: true } : msg
        )
      );

      const didSend = await sendMessage(option.label, {
        conversation_response: true,
        selected_action: option.action,
        payload: option.payload,
      });

      if (didSend) {
        setIsQuestionPending(false);
        return;
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.messageId === questionMessageId ? { ...msg, isAnswered: false } : msg
        )
      );
    },
    [setMessages, sendMessage]
  );

  const closeChat = () => {
    if (!enabled) {
      return;
    }

    // Invalidate in-flight stream callbacks before clearing so late setMessages
    // cannot resurrect bubbles after New Chat.
    chatGenerationRef.current += 1;
    resetStreamingRefs();
    setIsReplying(false);
    setIsQuestionPending(false);
    setMessages(() => []);
    setChatSessionId(null);
  };

  return {
    isReplying,
    isQuestionPending,
    messages,
    recentUserMessage,
    loadingStatusText,
    startChat,
    sendMessage,
    sendQuestionAnswer,
    closeChat,
  };
};
